import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import useGetRequest from '../../api/useGetRequest';
import ClientCreateWizard from './client-create-wizard';
import ClientSheet, { SHEET_SECTION } from './client-sheet';
import ClientsList from './clients-list';
import {
  EMPTY_FEATURES_CATALOG,
  FeaturesCatalogContext,
} from './feature-catalog';

const NEW_CLIENT = 'new';
const SECTIONS = Object.values(SHEET_SECTION);

/**
 * Une seule route (/internal/clients) pour la liste, la création et la fiche
 * d'un client : la section OUTILS INTERNES n'est protégée qu'au niveau des
 * routes de navigation, une route technique dédiée serait accessible sans
 * être SUPER-ADMIN.
 *
 * Paramètres : `client` (`new` ou un id), `section` (onglet de la fiche) et
 * `created` (fiche ouverte juste après la création). Le compte rendu de la
 * création (logo non envoyé, administrateurs refusés) passe par l'état de
 * navigation.
 */
const InternalClients = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const clientParam = searchParams.get('client');
  const sectionParam = searchParams.get('section');

  const { data: featuresCatalog } = useGetRequest(
    '/super-admin/clients/features-catalog',
    0
  );

  const openList = () => setSearchParams({});

  const clientId =
    clientParam && clientParam !== NEW_CLIENT ? Number(clientParam) : null;
  const isClientIdValid = Number.isInteger(clientId) && clientId > 0;

  const renderPage = () => {
    if (clientParam === NEW_CLIENT) {
      return (
        <ClientCreateWizard
          onExit={openList}
          onCreated={(createdClientId, creationReport) =>
            setSearchParams(
              {
                client: String(createdClientId),
                // Seul le logo a échoué : on ouvre l'onglet où le redéposer.
                section:
                  creationReport.isLogoMissing &&
                  creationReport.failedAdminEmails.length === 0
                    ? SHEET_SECTION.INFORMATION
                    : SHEET_SECTION.ADMINS,
                created: '1',
              },
              { replace: true, state: { creationReport } }
            )
          }
        />
      );
    }

    if (isClientIdValid) {
      return (
        <ClientSheet
          key={clientId}
          clientId={clientId}
          section={
            SECTIONS.includes(sectionParam)
              ? sectionParam
              : SHEET_SECTION.INFORMATION
          }
          onSectionChange={(section) =>
            setSearchParams(
              { client: String(clientId), section },
              { replace: true }
            )
          }
          isJustCreated={searchParams.get('created') === '1'}
          creationReport={location.state?.creationReport}
          onExit={openList}
        />
      );
    }

    return (
      <ClientsList
        onCreate={() => setSearchParams({ client: NEW_CLIENT })}
        onEdit={(id) => setSearchParams({ client: String(id) })}
      />
    );
  };

  return (
    <FeaturesCatalogContext.Provider
      value={featuresCatalog || EMPTY_FEATURES_CATALOG}
    >
      {renderPage()}
    </FeaturesCatalogContext.Provider>
  );
};

export default InternalClients;
